import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { useCartInfoStore, useCartStore } from '@entities/Cart'

import { ClearCartButton, ClearOutStockCartButton } from '@features/cart'
import { ProductCarouselNew } from '@features/products-carousel'

import CartBottomBar from '@widgets/cart-bottom-bar/CartBottomBar'
import { EmptyCart } from '@widgets/cart-empty'
import { CartEmptyInfo } from '@widgets/cart-empty-info'
import { CartTotal } from '@widgets/cart-total'
import { ProductCart } from '@widgets/product-cart'

import { useAnalytics } from '@shared/lib/analytics'
import { groupProductsByCategory } from '@shared/lib/groupProductsByCategory'
import BackButton from '@shared/ui/back-button'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './CartPage.module.scss'

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={3}
        width={'100%'}
        height={160}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function CartPage () {
  const { t } = useTranslation('common')
  const { t: tp } = useTranslation('product')
  const { t: tc } = useTranslation('cart')
  const {
    getTotalQuantity,
    getCart,
    getProduct,
    checkProductsInStock,
    isSyncing,
  } = useCartStore()

  const cart = getCart()

  const cartInfo = useCartInfoStore((state) => state.cartInfo)
  const cartInfoLoading = useCartInfoStore((state) => state.cartInfoLoading)
  const { viewCart: trackViewCart } = useAnalytics()

  const orderRef = useRef<HTMLDivElement>(null)
  const [ orderInView, setOrderInView ] = useState(false)
  const totalItems = getTotalQuantity()

  const totalAmount = useMemo(() => {
    return cartInfo?.totals?.total
  }, [ cartInfo ])

  // Track view_cart event when cart data is loaded
  useEffect(() => {
    if (cartInfo?.products?.length) {
      trackViewCart(cartInfo.products)
    }
  }, [ cartInfo?.products, trackViewCart ])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([ entry ]) => setOrderInView(entry.isIntersecting),
      { threshold: 0.1 },
    )
    const currentRef = orderRef.current
    if (currentRef) observer.observe(currentRef)
    return () => {
      if (currentRef) observer.unobserve(currentRef)
    }
  }, [])

  useEffect(() => {
    checkProductsInStock()
  }, [])

  const isSmallMobile = useMediaQuery('(max-width: 375px)')

  let asideContent
  if (totalItems) {
    asideContent = (
      <div ref={orderRef}>
        <Wrapper
          className={styles.orderWrapper}
          style={isSmallMobile ? { padding: '16px 8px' } : undefined}
        >
          <CartTotal />
        </Wrapper>

        {/* {!isMobile && (
          <Button
          >
            Поделиться корзиной
            <img
              src="/icons/share.svg"
              alt=""
              loading="lazy"
              fetchPriority="low"
            />
          </Button>
        )} */}
      </div>
    )
  }
  else {
    asideContent = (
      <div className={styles.asideStack}>
        <Wrapper>
          <CartEmptyInfo />
        </Wrapper>
      </div>
    )
  }

  const isLoading = useMemo(() => {
    // Keep showing data while background refetch runs
    return (cartInfoLoading && !cartInfo) || isSyncing
  }, [ cartInfoLoading, cartInfo, isSyncing ])

  const inStockProducts = useMemo(() => {
    const products = cartInfo?.products
    if (!products || !cart?.items) return []

    return products.filter((p) => {
      const cartItem = cart.items.find((i) => i.product_id === p?.id)
      return cartItem && cartItem.amount > 0 && !cartItem.isSoldOut
    })
  }, [ cartInfo?.products, cart?.items ])

  const outOfStockProducts = useMemo(() => {
    const products = cartInfo?.products
    if (!products || !cart?.items) return []

    return products.filter((p) => {
      const cartItem = cart.items.find((i) => i.product_id === p?.id)
      return cartItem?.isSoldOut || cartItem?.amount === 0
    })
  }, [ cartInfo?.products, cart?.items ])

  // Группируем товары в наличии по категориям
  const groupedInStockProducts = useMemo(() => {
    const productInfos = inStockProducts
      .map((p) => p?.info)
      .filter(Boolean)
    return groupProductsByCategory(productInfos)
  }, [ inStockProducts ])

  return (
    <div className={styles.root}>
      <Container>
        <PageGrid aside={asideContent}>
          <Wrapper>
            <div className={styles.root__grid}>
              <Breadcrumbs
                links={[
                  {
                    title: tc('title'),
                  },
                ]}
              />

              {!isLoading && (
                <div className={styles.root__top}>
                  <BackButton />

                  <TitleWithCount title={tc('title')}>
                    {totalItems && (
                      <>
                        {getTotalQuantity()}{' '}
                        {getNoun(
                          getTotalQuantity(),
                          tp('product.1'),
                          tp('product.2'),
                          tp('product.3'),
                        )}
                      </>
                    )}
                  </TitleWithCount>
                </div>
              )}

              {isLoading && <SkeletonLoading />}

              {!totalItems && <EmptyCart />}

              {totalItems && !!outOfStockProducts.length && !isLoading && (
                <div className={styles.root__group}>
                  <div className={styles['root__group-top']}>
                    {t('sold_out')}
                    <ClearOutStockCartButton />
                  </div>
                  <div className={styles.root__products}>
                    {outOfStockProducts &&
                      outOfStockProducts.map((p) => {
                        const product = getProduct(p.id)
                        if (!product) {
                          return (
                            <div></div>
                          )
                        }
                        const amount = product.amount

                        return (
                          <div
                            className={styles.root__item}
                            key={p.id}
                          >
                            <ProductCart
                              data={p.info}
                              amount={amount}
                              outStockProduct
                            />
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              {inStockProducts &&
                !!inStockProducts.length &&
                totalItems &&
                !isLoading && (
                <>
                  <div className={styles['root__group-header']}>
                    {tc('available')}
                    <ClearCartButton />
                  </div>
                  {groupedInStockProducts.map((group) => (
                    <div key={group.categoryName} className={styles.root__group}>
                      <div className={styles['root__group-top']}>
                        {group.categoryName}
                      </div>
                      <div className={styles.root__products}>
                        {group.products.map((product) => {
                          const cartProduct = getProduct(product.id)
                          const amount = cartProduct?.amount as number

                          return (
                            <div
                              className={styles.root__item}
                              key={product.id}
                            >
                              <ProductCart
                                data={product}
                                amount={amount}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {totalItems && (
                <div className={styles.root__carousel}>
                  <ProductCarouselNew />
                </div>
              )}
            </div>
          </Wrapper>
        </PageGrid>
        <AnimatePresence>
          {totalItems && !orderInView && totalAmount && (
            <CartBottomBar total={totalAmount} />
          )}
        </AnimatePresence>
      </Container>
    </div>
  )
}
